import { z } from 'zod';

import {
    doc,
    documentedStringEnum,
    itemCountField,
    moneyField,
    optionalDocumentedStringEnum,
    optionalExactLengthStringField,
    optionalIsoCodeField,
} from './openapiHelpers';
import { INSERT_TYPE_VALUES, PRODUCT_TYPE_VALUES, PRODUCT_TYPE_WRITE_VALUES } from '../fieldEnums';
import { SCAN_IMEI_SN_TYPE_CODES } from '../../constants/scanImeiSnType';

const expMethodEnum = documentedStringEnum(['FIFO', 'FEFO'], 'Stock rotation method (First-In-First-Out or First-Expired-First-Out).');

const productTypeWriteDescription =
    'Product type. pack = bundle/set requiring productSetItems. pallet = pallet packaging. virtual is deprecated — use serviceProduct.';

const productTypeResponseDescription =
    `${productTypeWriteDescription} item is a legacy type that may appear on GET.`;

const productTypeWriteEnum = documentedStringEnum(PRODUCT_TYPE_WRITE_VALUES, productTypeWriteDescription);

const productTypeResponseEnum = documentedStringEnum(PRODUCT_TYPE_VALUES, productTypeResponseDescription);

const scanImeiSnEnum = z.union([z.literal(0), z.literal(1), z.literal(2)]).describe(
    '@deprecated Use scanImeiSnType. Expedition-only scan mode: 0 = no scanning, 1 = serial numbers, 2 = IMEI. Mapped to none / snExpedition / imeiExpedition.',
);

const scanImeiSnTypeEnum = optionalDocumentedStringEnum(
    SCAN_IMEI_SN_TYPE_CODES,
    'When to scan IMEI or serial numbers. Optional — existing integrations may omit it. If omitted, deprecated scanImeiSn still applies; if that is also omitted, the product stays none. none = off. *Expedition = outbound only, *Inbound = receiving/returns only, *Both = both. imei* locks snRegex to ^[0-9]{14,16}$.',
);

const insertByOrderTypeEnum = documentedStringEnum(['all', 'b2c', 'b2b'], 'Insert product only for orders of this type.');

const batteryTypeEnum = documentedStringEnum(
    ['no', 'device', 'device_lr41', 'package', 'alone'],
    'Battery type for dangerous-goods/shipping rules.',
);

const productSetItemSchema = z.object({
    sku: doc(z.string().min(1), 'Component product SKU inside a pack/set.'),
    count: itemCountField(),
}).strict();

const productEanSchema = z.object({
    ean: doc(z.string().min(1), 'EAN barcode value.'),
    active: doc(z.boolean(), 'Whether this EAN is active — inactive EANs stay in history but are not used for scanning.'),
}).strict();

const productAttachmentTypeEnum = documentedStringEnum(
    ['instruction', 'label', 'other'],
    'Product attachment category.',
);

const productFileSchema = z.object({
    id: doc(z.number().int().min(1).optional(), 'Attachment ID when updating an existing file.'),
    fileName: doc(z.string().min(1), 'Original file name.'),
    fileType: doc(z.string().min(1), 'MIME type or extension.'),
    fileSize: doc(z.string().nullish(), 'File size.'),
    type: productAttachmentTypeEnum,
    data: doc(z.string().min(1).optional(), 'Base64 content. Required for new files.'),
    createdAt: doc(z.union([z.string(), z.date()]).nullish(), 'Upload timestamp from files.CREATED_AT.'),
    creatorRole: doc(
        z.enum(['skladon_employee', 'client']).nullish(),
        'Who uploaded the file (Skladon employee or client).',
    ),
}).strict();

type ProductSetItemsInput = z.infer<typeof productSetItemSchema>[] | undefined;

function hasNonEmptyProductSetItems(items: ProductSetItemsInput): boolean {
    return Array.isArray(items) && items.length > 0;
}

const productSetItemsOnlyForPackIssue: { message: string; path: (string | number)[] } = {
    message:
        'productSetItems are only allowed when productType is "pack" (bundle / set). For other types, omit productSetItems.',
    path: ['productSetItems'],
};

const packRequiresProductSetItemsIssue: { message: string; path: (string | number)[] } = {
    message:
        'productType "pack" (bundle / set) requires productSetItems: at least one line with sku and count for each component product.',
    path: ['productSetItems'],
};

const patchPackRequiresProductSetItemsIssue: { message: string; path: (string | number)[] } = {
    message:
        'When setting productType to "pack", productSetItems is required with at least one component line (sku and count).',
    path: ['productSetItems'],
};

export const productV21BaseSchema = z.object({
    sku: doc(z.string().min(1), 'Unique product SKU in your catalog.'),
    guid: doc(z.string().nullish(), 'Optional stable product GUID.'),
    name: doc(z.string().min(1), 'Product display name.'),
    description: doc(z.string().nullish(), 'Long product description.'),
    price: moneyField(),
    hsCode: doc(z.string().nullish(), 'Harmonized System (HS) customs code.'),
    upc: doc(z.string().nullish(), 'UPC barcode.'),
    ean: doc(z.string().nullish(), 'EAN barcode.'),
    eans: doc(
        z.array(productEanSchema).optional(),
        'All EANs of the product (active + inactive). The array replaces the current list. Ignored for products with eanLockedByDcix = 1 unless your API key has the EAN edit permission.',
    ),
    rop: doc(z.number().int().default(0), 'Reorder point — minimum stock level trigger. If omitted on create, defaults to 0.'),
    expMethod: expMethodEnum,
    mpn: doc(z.string().nullish(), 'Manufacturer part number.'),
    isbn: doc(z.string().nullish(), 'ISBN for books.'),
    note: doc(z.string().nullish(), 'Internal product note.'),
    productType: productTypeWriteEnum,
    customField1: doc(z.string().nullish(), 'Custom field 1 (eshop-specific).'),
    customField2: doc(z.string().nullish(), 'Custom field 2 (eshop-specific).'),
    customField3: doc(z.string().nullish(), 'Custom field 3 (eshop-specific).'),
    ageCheck: doc(z.boolean().default(false), 'Require age verification at delivery. If omitted on create, defaults to false.'),
    active: doc(z.boolean(), 'Whether the product is active for new orders.'),
    scanImeiSn: scanImeiSnEnum.optional(),
    scanImeiSnType: scanImeiSnTypeEnum,
    originCountry: optionalExactLengthStringField(
        2,
        'Country of origin (ISO 3166-1 alpha-2, exactly 2 letters, for example PL). Null if unknown.',
    ),
    isDeleted: doc(z.boolean().optional(), 'Soft-delete flag.'),
    batteryType: batteryTypeEnum.nullish(),
    snRegex: doc(z.string().default(''), 'Regex validating serial numbers when the scan type uses serial numbers. Ignored for imei* types (locked regex).'),
    unitPrice: optionalIsoCodeField(
        3,
        'Currency of the product (ISO 4217, 3 letters, for example CZK). The name unitPrice is historical — this is not a numeric amount. The amount is price. Empty string or null means no currency is set.',
    ),
    expirationDays: doc(z.number().int().default(0), 'Shelf-life in days when the lot has no expiration date. If omitted on create, defaults to 0.'),
    productSetItems: doc(
        z.array(productSetItemSchema).optional(),
        'Component lines for productType pack — required on create and when PATCH sets productType to pack.',
    ),
    files: doc(z.array(productFileSchema).optional(), 'Product attachments (instructions, labels, …).'),
}).strict();

export const createProductV21Schema = productV21BaseSchema
    .refine(
        (data) => !hasNonEmptyProductSetItems(data.productSetItems) || data.productType === 'pack',
        productSetItemsOnlyForPackIssue,
    )
    .refine(
        (data) => data.productType !== 'pack' || hasNonEmptyProductSetItems(data.productSetItems),
        packRequiresProductSetItemsIssue,
    );

export const patchProductV21Schema = productV21BaseSchema
    .partial()
    .refine((data) => Object.keys(data).length > 0, { message: 'At least one field must be provided' })
    .refine(
        (data) =>
            !hasNonEmptyProductSetItems(data.productSetItems) ||
            data.productType === undefined ||
            data.productType === 'pack',
        productSetItemsOnlyForPackIssue,
    )
    .refine(
        (data) =>
            data.productType !== 'pack' || hasNonEmptyProductSetItems(data.productSetItems),
        patchPackRequiresProductSetItemsIssue,
    );

const readOnlyFields = z.object({
    id: doc(z.number().int(), 'Internal Skladon product ID.'),
    eanLockedByDcix: doc(
        z.number().int().default(0),
        'Read-only. 1 = EANs of this product are managed by Skladon, so ean and eans sent by the eshop are ignored unless your API key has the EAN edit permission.',
    ),
    weight: doc(z.number().nullish(), 'Weight in kg.'),
    width: doc(z.number().nullish(), 'Width in cm.'),
    height: doc(z.number().nullish(), 'Height in cm.'),
    length: doc(z.number().nullish(), 'Length in cm.'),
    insertToProductSkus: doc(z.array(z.string()).default([]), 'SKUs this insert product is attached to.'),
    insertToCountries: doc(
        z.array(z.string()).default([]),
        'ISO 3166-1 alpha-2 country codes where this insert product applies.',
    ),
    insertType: optionalDocumentedStringEnum(INSERT_TYPE_VALUES, 'Insert product targeting mode (read-only).'),
    insertByOrderType: insertByOrderTypeEnum.nullish(),
    insertPriceFrom: doc(z.number().nullish(), 'Minimum order price for insert eligibility.'),
    insertPriceTo: doc(z.number().nullish(), 'Maximum order price for insert eligibility.'),
    createdAt: doc(z.string(), 'Record creation timestamp.'),
    updatedAt: doc(z.string(), 'Last update timestamp.'),
    dimensionsChangedAt: doc(z.string().nullish(), 'When dimensions were last changed.'),
    deletedAt: doc(z.string().nullish(), 'Soft-delete timestamp.'),
    quantityConversion: doc(z.number().int().default(1), 'Quantity conversion factor between eshop and WMS units.'),
    sourceSys: doc(z.string().nullish(), 'System the product record originated from.'),
}).strict();

export const productV21ResponseSchema = productV21BaseSchema
    .omit({ productType: true })
    .merge(readOnlyFields)
    .extend({
        productType: productTypeResponseEnum,
    });

export type ProductV21 = z.infer<typeof productV21ResponseSchema>;
export type CreateProductV21 = z.infer<typeof createProductV21Schema>;
export type PatchProductV21 = z.infer<typeof patchProductV21Schema>;

const availabilityQuantityFields = {
    available: z.number().nullable().describe(
        'Quantity available for sale. Not derived from other fields — comes from WMS and may be adjusted by an availability coefficient, so all - allocated - onHoldQuantity does not have to equal available.',
    ),
    all: z.number().nullable().describe('Total quantity physically in the warehouse.'),
    allocated: z.number().nullable().describe('Quantity allocated to orders that have not been shipped yet.'),
    onHoldQuantity: z.number().nullable().describe('Quantity blocked by quality control.'),
    dutyQuantity: z.number().nullable().describe('Quantity held in the customs warehouse.'),
    onExpedition: z.number().nullable().describe('Quantity already picked and waiting for expedition.'),
    awaiting: z.number().nullable().describe('Quantity expected from receivings that have not been received yet.'),
};

export const productAvailabilityStockLineV21Schema = z.object({
    ...availabilityQuantityFields,
    expirationDate: z.string().nullable().describe('Expiration date of this stock line (when sendItems=true).'),
    lot: z.string().nullable().describe('Lot/batch of this stock line (when sendItems=true).'),
}).strict();

export const productAvailabilityItemV21Schema = z.object({
    sku: z.string(),
    ...availabilityQuantityFields,
    error: z.string().nullable().describe(
        'When set, the SKU is not in the eshop catalog and all quantity fields are null. Soft-deleted products still return quantities. Otherwise null.',
    ),
    items: z.array(productAvailabilityStockLineV21Schema).optional().describe(
        'Present when sendItems=true — per stock-line rows with expirationDate and lot.',
    ),
}).strict();

export const productAvailabilitySkusRequestV21Schema = z.object({
    skus: doc(z.array(z.string().min(1)).min(1), 'List of product SKUs to check availability for.'),
}).strict();

export type ProductAvailabilityItemV21 = z.infer<typeof productAvailabilityItemV21Schema>;
export type ProductAvailabilitySkusRequestV21 = z.infer<typeof productAvailabilitySkusRequestV21Schema>;

